import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Shield,
  Upload,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  Loader2,
  Building2,
  AlertTriangle,
  ChevronDown,
  Camera,
  Fingerprint,
  Search,
  Scale,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";

const BRANDS = {
  financify: {
    name: "Financify",
    logo: null,
    logoText: true,
    primary: "#52BFB8",
    primaryDark: "#3DA8A1",
    bg: "#0a1628",
    surface: "#111d32",
    welcome: "Welcome to Financify Verify",
    subtitle: "Complete your identity verification to proceed with your application.",
    support: "support@financify-caribbean.com",
    accent: "#FDD552",
  },
  coral: {
    name: "Coral Bank",
    logo: null,
    logoText: true,
    primary: "#E85D75",
    primaryDark: "#D04A62",
    bg: "#1a0f14",
    surface: "#2a1520",
    welcome: "Welcome to Coral Bank Onboarding",
    subtitle: "Verify your identity securely to open your new account.",
    support: "onboarding@coralbank.bs",
    accent: "#FF9EB5",
  },
  anchor: {
    name: "Anchor Credit Union",
    logo: null,
    logoText: true,
    primary: "#3B82F6",
    primaryDark: "#2563EB",
    bg: "#0a1528",
    surface: "#111a32",
    welcome: "Anchor Credit Union — Member Verification",
    subtitle: "Help us verify your identity to complete your membership application.",
    support: "members@anchorcu.bs",
    accent: "#93C5FD",
  },
  bluereef: {
    name: "Blue Reef Insurance",
    logo: null,
    logoText: true,
    primary: "#06B6D4",
    primaryDark: "#0891B2",
    bg: "#0a1620",
    surface: "#0f1f2e",
    welcome: "Blue Reef Insurance — Policyholder Verification",
    subtitle: "Quick identity check before we issue your policy.",
    support: "verify@bluereef.bs",
    accent: "#67E8F9",
  },
  harbour: {
    name: "Harbour Auto Finance",
    logo: null,
    logoText: true,
    primary: "#F59E0B",
    primaryDark: "#D97706",
    bg: "#1a1508",
    surface: "#2a2210",
    welcome: "Harbour Auto Finance — Applicant Verification",
    subtitle: "Verify your identity to proceed with your vehicle financing.",
    support: "apply@harbourfinance.bs",
    accent: "#FCD34D",
  },
};

const RISK_OUTCOMES = [
  { label: "Low Risk", color: "text-emerald-400", bg: "bg-emerald-500/15" },
  { label: "Review Required", color: "text-amber-400", bg: "bg-amber-500/15" },
  { label: "High Risk", color: "text-red-400", bg: "bg-red-500/15" },
];

export default function PartnersDemoPage() {
  const navigate = useNavigate();
  const [brandKey, setBrandKey] = useState("financify");
  const [step, setStep] = useState(0); // 0=intro, 1=details, 2=docs, 3=verifying, 4=result
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [riskIndex, setRiskIndex] = useState(0);

  const brand = BRANDS[brandKey];

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", dob: "", address: "",
    idFront: null, idBack: null, selfie: null,
  });

  // Simulate verification delay
  useEffect(() => {
    if (step === 3) {
      const rand = Math.random();
      const risk = rand < 0.6 ? 0 : rand < 0.9 ? 1 : 2;
      setRiskIndex(risk);
      const timer = setTimeout(() => setStep(4), 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const resetDemo = () => {
    setStep(0);
    setForm({ firstName: "", lastName: "", email: "", phone: "", dob: "", address: "", idFront: null, idBack: null, selfie: null });
  };

  const switchBrand = (key) => {
    setBrandKey(key);
    setShowBrandPicker(false);
    resetDemo();
  };

  const risk = RISK_OUTCOMES[riskIndex];

  return (
    <div className="min-h-screen" style={{ background: brand.bg, fontFamily: "Inter, sans-serif" }}>

      {/* Brand Selector Bar */}
      <div className="sticky top-0 z-50 border-b" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/partners/verify")}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1"
            data-testid="back-to-partners"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>

          <div className="relative">
            <button
              onClick={() => setShowBrandPicker(!showBrandPicker)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ background: `${brand.primary}15`, color: brand.primary }}
              data-testid="brand-selector"
            >
              <Building2 className="w-3.5 h-3.5" />
              {brand.name}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showBrandPicker && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-2xl border overflow-hidden z-50" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.1)" }}>
                {Object.entries(BRANDS).map(([key, b]) => (
                  <button
                    key={key}
                    onClick={() => switchBrand(key)}
                    data-testid={`brand-${key}`}
                    className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition-colors ${
                      key === brandKey ? "text-white" : "text-slate-400 hover:text-white"
                    }`}
                    style={key === brandKey ? { background: `${b.primary}20` } : { }}
                  >
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: b.primary }} />
                    {b.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className="text-[10px] text-slate-600 tracking-wider uppercase">Live Demo</span>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8 md:py-12">

        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3" style={{ background: `${brand.primary}20` }}>
            <Shield className="w-6 h-6" style={{ color: brand.primary }} />
          </div>
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            {brand.name}
          </h1>
        </div>

        {/* Progress bar */}
        {step > 0 && step < 4 && (
          <div className="flex items-center gap-1 mb-8" data-testid="demo-progress">
            {["Details", "Documents", "Verifying"].map((label, i) => (
              <div key={i} className="flex-1">
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: step > i + 1 ? "100%" : step === i + 1 ? "50%" : "0%", background: brand.primary }}
                  />
                </div>
                <p className="text-[10px] mt-1 text-center" style={{ color: step >= i + 1 ? brand.primary : "rgb(100,116,139)" }}>{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Step 0: Intro */}
        {step === 0 && (
          <div className="rounded-2xl p-6 md:p-8 text-center border" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.06)" }} data-testid="step-intro">
            <h2 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              {brand.welcome}
            </h2>
            <p className="text-sm text-slate-400 mb-6">{brand.subtitle}</p>

            <div className="space-y-3 text-left max-w-sm mx-auto mb-8">
              {[
                { icon: User, text: "Provide your personal details" },
                { icon: FileText, text: "Upload a government-issued ID" },
                { icon: Camera, text: "Take a selfie for liveness check" },
                { icon: Shield, text: "Automated screening & verification" },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${brand.primary}15` }}>
                    <Icon className="w-4 h-4" style={{ color: brand.primary }} />
                  </div>
                  <span className="text-sm text-slate-300">{text}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => setStep(1)}
              className="w-full py-5 text-base font-semibold rounded-xl text-white"
              style={{ background: brand.primary }}
              data-testid="start-verification-btn"
            >
              Start Verification
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-[11px] text-slate-600 mt-4">
              Need help? <span style={{ color: brand.primary }}>{brand.support}</span>
            </p>
          </div>
        )}

        {/* Step 1: Applicant Details */}
        {step === 1 && (
          <div className="rounded-2xl p-6 md:p-8 border" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.06)" }} data-testid="step-details">
            <h2 className="text-base font-semibold text-white mb-5" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Applicant Details
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> First Name</label>
                  <input
                    value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 border outline-none transition-colors"
                    style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.1)" }}
                    placeholder="John" data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Last Name</label>
                  <input
                    value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 border outline-none transition-colors"
                    style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.1)" }}
                    placeholder="Rolle" data-testid="input-last-name"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                <input
                  type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 border outline-none transition-colors"
                  style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.1)" }}
                  placeholder="john@example.com" data-testid="input-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</label>
                  <input
                    value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 border outline-none transition-colors"
                    style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.1)" }}
                    placeholder="(242) 555-0000" data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date of Birth</label>
                  <input
                    type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 border outline-none transition-colors"
                    style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.1)" }}
                    data-testid="input-dob"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</label>
                <input
                  value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 border outline-none transition-colors"
                  style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.1)" }}
                  placeholder="123 Bay Street, Nassau" data-testid="input-address"
                />
              </div>
            </div>
            <Button
              onClick={() => setStep(2)}
              className="w-full mt-6 py-5 text-base font-semibold rounded-xl text-white"
              style={{ background: brand.primary }}
              data-testid="next-to-docs-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Document Upload */}
        {step === 2 && (
          <div className="rounded-2xl p-6 md:p-8 border" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.06)" }} data-testid="step-docs">
            <h2 className="text-base font-semibold text-white mb-2" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Upload Documents
            </h2>
            <p className="text-xs text-slate-500 mb-5">Upload a government-issued ID and take a selfie for verification.</p>

            <div className="space-y-3">
              {[
                { key: "idFront", label: "ID Front", icon: FileText, desc: "Passport, Driver's License, or National ID" },
                { key: "idBack", label: "ID Back", icon: FileText, desc: "Back side of your ID document" },
                { key: "selfie", label: "Selfie", icon: Camera, desc: "Clear photo of your face for liveness check" },
              ].map(({ key, label, icon: Icon, desc }) => (
                <button
                  key={key}
                  onClick={() => setForm(f => ({ ...f, [key]: `${label}_uploaded.jpg` }))}
                  data-testid={`upload-${key}`}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left"
                  style={{
                    background: form[key] ? `${brand.primary}10` : brand.bg,
                    borderColor: form[key] ? `${brand.primary}40` : "rgba(255,255,255,0.06)",
                  }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: form[key] ? `${brand.primary}20` : "rgba(255,255,255,0.04)" }}>
                    {form[key] ? (
                      <CheckCircle2 className="w-5 h-5" style={{ color: brand.primary }} />
                    ) : (
                      <Icon className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: form[key] ? brand.primary : "rgb(226,232,240)" }}>{label}</p>
                    <p className="text-[11px] text-slate-600">{form[key] ? "Uploaded" : desc}</p>
                  </div>
                  {!form[key] && <Upload className="w-4 h-4 text-slate-600 flex-shrink-0" />}
                </button>
              ))}
            </div>

            <Button
              onClick={() => setStep(3)}
              className="w-full mt-6 py-5 text-base font-semibold rounded-xl text-white"
              style={{ background: brand.primary }}
              data-testid="submit-docs-btn"
            >
              Submit for Verification
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Verifying */}
        {step === 3 && (
          <div className="rounded-2xl p-8 md:p-12 text-center border" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.06)" }} data-testid="step-verifying">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 animate-pulse" style={{ background: `${brand.primary}20` }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: brand.primary }} />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Verification in Progress
            </h2>
            <p className="text-sm text-slate-400 mb-6">Running identity checks, screening, and document analysis...</p>

            <div className="space-y-3 max-w-xs mx-auto">
              {[
                { label: "Document authenticity", delay: 0 },
                { label: "ID data extraction", delay: 0.5 },
                { label: "Liveness detection", delay: 1 },
                { label: "Sanctions screening", delay: 1.5 },
                { label: "PEP database check", delay: 2 },
              ].map(({ label, delay }, i) => (
                <VerifyingRow key={i} label={label} delay={delay} color={brand.primary} />
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && (
          <div className="rounded-2xl p-6 md:p-8 border" style={{ background: brand.surface, borderColor: "rgba(255,255,255,0.06)" }} data-testid="step-result">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: `${brand.primary}15` }}>
                <Shield className="w-7 h-7" style={{ color: brand.primary }} />
              </div>
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Verification Complete
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {form.firstName || "John"} {form.lastName || "Rolle"} — {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            </div>

            {/* Check results */}
            <div className="space-y-2 mb-6">
              {[
                { label: "ID Verification", result: "Passed", icon: Fingerprint, passed: true },
                { label: "Liveness Check", result: "Passed", icon: Camera, passed: true },
                { label: "Sanctions Screening", result: "Clear", icon: Search, passed: true },
                { label: "PEP Screening", result: "No Match", icon: Shield, passed: true },
              ].map(({ label, result, icon: Icon, passed }, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl border" style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.04)" }} data-testid={`result-${label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-300">{label}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${passed ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                    {result}
                  </span>
                </div>
              ))}
            </div>

            {/* Risk recommendation */}
            <div className="rounded-xl p-4 border mb-4" style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.04)" }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">Risk Recommendation</span>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${risk.bg} ${risk.color}`} data-testid="risk-recommendation">
                  {risk.label}
                </span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                <AlertTriangle className="w-3.5 h-3.5 text-slate-600" />
                <span className="text-[11px] text-slate-500">Final decision: <span className="text-slate-400 font-medium">Made by partner institution</span></span>
              </div>
            </div>

            {/* Powered by */}
            <div className="text-center pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
              <p className="text-[10px] text-slate-600 mb-4">
                Powered by <span style={{ color: brand.primary }}>Financify Verify</span>
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={resetDemo}
                  variant="outline"
                  className="flex-1 border-white/10 text-slate-400 hover:bg-white/5 text-sm"
                  data-testid="restart-demo-btn"
                >
                  Restart Demo
                </Button>
                <Button
                  onClick={() => navigate("/book-demo")}
                  className="flex-1 text-sm font-semibold text-white"
                  style={{ background: brand.primary }}
                  data-testid="schedule-demo-btn"
                >
                  Schedule a Demo
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Animated verification row — shows spinner then checkmark */
function VerifyingRow({ label, delay, color }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), (delay + 0.8) * 1000);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-400">{label}</span>
      {done ? (
        <CheckCircle2 className="w-4 h-4" style={{ color }} />
      ) : (
        <Clock className="w-4 h-4 text-slate-600 animate-pulse" />
      )}
    </div>
  );
}
